import { Grid, GridColumn, GridRow, Input, Select, SelectOptionType } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { CombinedError } from 'urql';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import {
  AdditionalTeachingLoadCreateInput,
  AdditionalTeachingLoadUpdateInput,
  useAdditionalTeachingLoadsListQuery,
  useCreateAdditionalTeachingLoadsMutation,
  useUpdateAdditionalTeachingLoadMutation,
} from '../../../types/planung-graphql-client-defs';
import { interpreteAsNumber } from '../../../utils/helper';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import { FactorInterpretationInfo } from '../../Factors/forms/components/FactorInterpretationInfo';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { schema } from './AdditionalDeputateForm.schema';

type AdditionalDeputateFormType = {
  hoursRaw: string | undefined;
  hours: string;
  comment: string;
  teacherName: string;
  teacherUuid: string;
  subjectName: string;
  subjectUuid?: string;
  className: string;
  classUuid?: string;
};

type AdditionalDeputateFormProps = {
  deputatUuid: string | null;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
};

export const AdditionalDeputateForm = ({ deputatUuid, onLoad, onClose }: AdditionalDeputateFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useUserConfigContext().selectedSchoolYear;
  const context = useMemorizedCacheTag('ADDITIONAL_TEACHINGLOAD');

  const { teacherData, subjectData, classesData } = useLoadBasicData({ pause: false });

  const [{ data }] = useAdditionalTeachingLoadsListQuery({
    variables: { organizationUuid: pimAuthClaims.getOrganizationUuid(), schoolyear: schoolYear?.uuid ?? '' },
    context,
  });

  const selectedDeputat = data?.additionalTeachingLoads.find((d) => d.uuid === deputatUuid);

  const [, createAdditionalTeachingLoadForm] = useCreateAdditionalTeachingLoadsMutation();
  const [, updateAdditionalTeachingLoadForm] = useUpdateAdditionalTeachingLoadMutation();

  const teacherOpts = useCreateSelectOptions(teacherData?.people, 'uuid', 'listName');
  const subjectOpts = useCreateSelectOptions(subjectData?.subjects, 'uuid', 'name');
  const gradeGroupsOptions = useCreateSelectOptions(classesData?.classes, 'uuid', 'shortName');

  const handleSubmit = async (
    values: AdditionalDeputateFormType,
    helper: FormikHelpers<AdditionalDeputateFormType>,
  ) => {
    onLoad(true);

    let result: { error?: CombinedError | undefined } = { error: undefined };

    // update
    if (selectedDeputat) {
      const updateInput: AdditionalTeachingLoadUpdateInput = {
        teacher: { disconnect: {}, connect: { where: { node: { uuid: values.teacherUuid } } } },
        hours: parseFloat(values.hours),
        hoursRaw: values.hoursRaw ?? values.hours,
      };

      updateInput.comment = '';
      values.comment && (updateInput.comment = values.comment);

      updateInput.class = { disconnect: {} };
      values.classUuid &&
        (updateInput.class = { disconnect: {}, connect: { where: { node: { uuid: values.classUuid } } } });

      updateInput.subject = { disconnect: {} };
      values.subjectUuid &&
        (updateInput.subject = { disconnect: {}, connect: { where: { node: { uuid: values.subjectUuid } } } });

      result = await updateAdditionalTeachingLoadForm({ update: updateInput, uuid: selectedDeputat.uuid }, context);

      if (result.error) {
        showUserErrorToast({ error: result.error });
      } else {
        showSuccessUpdateToast();
      }
    }

    // create
    if (!selectedDeputat) {
      const createInput: AdditionalTeachingLoadCreateInput = {
        organization: { connect: { where: { node: { uuid: pimAuthClaims.getOrganizationUuid() } } } },
        schoolYear: { connect: { where: { node: { uuid: schoolYear?.uuid } } } },
        teacher: { connect: { where: { node: { uuid: values.teacherUuid } } } },
        hours: parseFloat(values.hours),
        hoursRaw: values.hoursRaw ?? values.hours,
      };

      values.comment && (createInput.comment = values.comment);
      values.classUuid && (createInput.class = { connect: { where: { node: { uuid: values.classUuid } } } });
      values.subjectUuid && (createInput.subject = { connect: { where: { node: { uuid: values.subjectUuid } } } });

      result = await createAdditionalTeachingLoadForm({ input: createInput }, context);
      if (result.error) {
        showUserErrorToast({ error: result.error });
      } else {
        showSuccessCreateToast();
      }
    }

    helper.resetForm();
    onClose();
    onLoad(false);
  };

  const initialValues: AdditionalDeputateFormType = {
    teacherUuid: selectedDeputat?.teacher.uuid ?? '',
    teacherName: selectedDeputat?.teacher.listName ?? '',
    subjectUuid: selectedDeputat?.subject?.uuid ?? '',
    subjectName: selectedDeputat?.subject?.name ?? '',
    classUuid: selectedDeputat?.class?.uuid ?? '',
    className: selectedDeputat?.class?.name ?? '',
    comment: selectedDeputat?.comment ?? '',
    hours: selectedDeputat?.hours.toString() ?? '',
    hoursRaw: selectedDeputat?.hoursRaw?.toString() ?? selectedDeputat?.hours?.toString(),
  };

  return (
    <Formik<AdditionalDeputateFormType> initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
      {({ values, isSubmitting, dirty, resetForm, handleChange, setFieldValue, setFieldTouched, errors }) => (
        <Form>
          <Grid useFormGap>
            <GridRow spacingBottom='s'>
              <GridColumn width={5}>
                <Select
                  name='teacherUuid'
                  isSearchable
                  label={t('persons.title', { count: 1 })}
                  value={teacherOpts.find((to) => to.value === values?.teacherUuid)}
                  options={teacherOpts}
                  onChange={async (option) => {
                    const opt = option as SingleValue<SelectOptionType>;
                    await setFieldTouched(`teacherUuid`, true);
                    await setFieldValue(`teacherUuid`, opt?.value);
                  }}
                  error={errors.teacherUuid}
                />
              </GridColumn>
              <GridColumn width={5}>
                <Select
                  isClearable
                  isSearchable
                  name='subjectUuid'
                  value={subjectOpts.find((to) => to.value === values?.subjectUuid)}
                  label={t('subject.title', { count: 1 })}
                  options={subjectOpts}
                  onChange={async (option) => {
                    const opt = option as SingleValue<SelectOptionType>;
                    await setFieldTouched(`subjectUuid`, true);
                    await setFieldValue(`subjectUuid`, opt?.value);
                  }}
                />
              </GridColumn>
              <GridColumn width={2}>
                <Select
                  isClearable
                  isSearchable
                  name='classUuid'
                  label={t('classes.title', { count: 1 })}
                  value={gradeGroupsOptions.find((to) => to.value === values?.classUuid)}
                  options={gradeGroupsOptions}
                  onChange={async (option) => {
                    const opt = option as SingleValue<SelectOptionType>;
                    await setFieldTouched(`classUuid`, true);
                    await setFieldValue(`classUuid`, opt?.value);
                  }}
                />
              </GridColumn>
            </GridRow>

            <GridRow spacingTop='s' spacingBottom='s'>
              <GridColumn width={8}>
                <Input name='comment' value={values.comment} onChange={handleChange} label={t('common.comment')} />
              </GridColumn>
              <GridColumn width={2}>
                <Input
                  value={values.hoursRaw}
                  name='hours'
                  onChange={async (e) => {
                    await setFieldValue('hoursRaw', e.target.value);
                    await setFieldValue('hours', interpreteAsNumber(e.target.value));
                    await setFieldTouched('hours');
                  }}
                  type='number'
                  label={t('common.hours')}
                  error={errors.hoursRaw}
                />
              </GridColumn>
              <GridColumn width={2} align='end'>
                <FactorInterpretationInfo value={interpreteAsNumber(values.hours)} />
              </GridColumn>
            </GridRow>
          </Grid>

          <ModalBottomButtons
            closeButton={{
              callback: () => {
                resetForm();
                onClose();
              },
            }}
            submitButton={{ disabled: isSubmitting || !dirty }}
          />
        </Form>
      )}
    </Formik>
  );
};
